import React, { useContext, useMemo } from "react";
import { Intro, Text } from "components/commons";
import useDataTable from "hooks/useDataTable";
import locale from "localization";
import DataTableV2 from "components/commons/data-table/data-table-v2";
import useExport from "hooks/useExport";
import { generateReportAdmin } from "apis";
import { getPayments, voidPayment } from "apis/pay-with-peso.api";
import { payWithPesoFilterState } from "./pay-with-peso-filter.state";
import { mapDataToList, mapFilterToRequest } from "./pay-with-peso.mapper";
import { payWithPesoColumns } from "./pay-with-peso-columns";
import PayWithPesoFilter from "./pay-with-peso-filter";
import { ConfirmModal, SuccessModal } from "components/modals";
import { UserContext } from "contexts";
import { useApi, useModal } from "hooks";
import { ErrorCode, SettlementStatus } from "enums";
import { formatAmount } from "utils";
import { useState } from "react";
import { useCallback } from "react";
import VoidPaymentModal from "./void-payment-modal";
import ActionsHistoryModal from "./actions-history-modal";
import DiscountType from "enums/discount-type";
import { UserAccess } from "enums";

const PayWithPesoModule = () => {
  const voidPaymentModal = useModal();
  const actionsHistoryModal = useModal();
  const confirmModal = useModal();

  const { sessionId, userAccess } = useContext(UserContext);

  const getUserAccess = userAccess.find((item) => {
    return item.key === UserAccess.PLCLOCQPayForFuel;
  });
  const [remarks, setRemarks] = useState("");
  const [value, setValue] = useState("");

  const columns = useMemo(() => {
    return (getUserAccess?.fullAccess || getUserAccess?.modifyAccess)
      ? payWithPesoColumns
      : payWithPesoColumns.filter((e) => e.key !== "action");
  }, [getUserAccess]);

  const { request: voidRequest, loading: voidingRequest } = useApi({
    api: voidPayment,
    pageError: false,
    modalError: true,
    handleOwnError: true,
  });

  const handleVoidPayment = ({
    paymentId,
    amount,
    rebate,
    settlementId,
    settlementStatus,
    discountType,
    totalDiscount,
  }) => {
    setRemarks("");
    voidPaymentModal.show({
      title: `${locale.voidTransaction}?`,
      description: (
        <Text align="center">
          <locale.Populate
            text={
              settlementId
                ? settlementStatus === SettlementStatus.ForProcessing
                  ? locale.voidingThisTransactionSettlementProcessing
                  : locale.voidingThisTransactionSettlementProcessed
                : locale.voidingThisPayment
            }
            items={[
              <b>{formatAmount(amount)}</b>,
              <b>
                {formatAmount(discountType === DiscountType.Outright ? totalDiscount : rebate)}
              </b>,
            ]}
          />
        </Text>
      ),
      label: locale.reasonRemarks,
      actionText: locale.voidTransaction,
      submit: (value) => {
        onVoidPaymentCb({
          paymentId,
          value,
          amount,
        });
      },
    });
  };

  const handleActionsHistory = ({ voidedByEmail, status, voidedAt }) => {
    actionsHistoryModal.show({
      title: locale.actionsHistory,
      voidedByEmail,
      status,
      voidedAt,
    });
  };

  const onVoidPaymentCb = useCallback(
    async ({ paymentId, value, amount, totalDiscount }) => {
      try {
        await voidRequest({ paymentId, remarks: value });
        voidPaymentModal.close();
        table.fetch({});

        confirmModal.show({
          title: locale.transactionVoided,
          content: (
            <locale.Populate
              text={locale.thisPaymentHasBeenVoided}
              items={[<b>{formatAmount(amount)}</b>]}
            />
          ),
          loading: voidingRequest,
          primary: {
            text: locale.gotIt,
            onClick: () => {
              confirmModal.close();
            },
          },
        });
        setRemarks("");
      } catch (err) {
        const errorCode =
          err?.data?.errorCode || err?.data?.code || err?.data?.messageCode || err?.errorCode;

        if (
          errorCode === ErrorCode.TxnVoidingWindowExpired ||
          err?.data?.message?.includes?.(ErrorCode.TxnVoidingWindowExpired)
        ) {
          voidPaymentModal.close();
          confirmModal.show({
            title: locale.unableToVoidTransaction,
            content: locale.voidingWindowExpiredMessage,
            primary: {
              text: locale.gotIt,
              onClick: () => {
                confirmModal.close();
              },
            },
          });
          return;
        }

        return err.showError?.();
      }
    },
    //eslint-disable-next-line
    [voidRequest, voidPaymentModal, confirmModal, voidingRequest]
  );

  const { filter, search, table } = useDataTable({
    api: { api: getPayments },
    filter: { initialState: payWithPesoFilterState(), mapper: mapFilterToRequest },
    table: {
      key: "payments",
      mapper: (payments) =>
        mapDataToList({ payments, handleVoidPayment, handleActionsHistory, getUserAccess }),
      columns,
    },
  });

  const exports = useExport({
    api: generateReportAdmin,
    hasModal: true,
    reportType: "payment",
    mappedFilterState: {
      ...filter?.mappedFilterState,
      searchKey: search.searchKey,
      connectionId: sessionId,
    },
  });

  delete table.result.count;

  return (
    <>
      <SuccessModal {...exports?.successModalComponent} />
      <ConfirmModal {...confirmModal} />
      <div>
        <Intro title={locale.locqPayForFuel} subtitle={locale.viewAndTrackScanToPayLocqPay} />
      </div>
      <PayWithPesoFilter {...filter} {...search} {...exports} getUserAccess={getUserAccess} />
      <DataTableV2 {...table} />
      <ActionsHistoryModal {...actionsHistoryModal} />
      <VoidPaymentModal
        {...voidPaymentModal}
        remarks={remarks}
        onChangeRemarks={(v) => {
          setRemarks(v ? v : "");
        }}
        loading={voidingRequest}
        onSubmit={voidPaymentModal.submit}
        value={value}
        setValue={setValue}
      />
    </>
  );
};

export default PayWithPesoModule;
